// 15.D — тред консультации: заказчик общается здесь, координатор отвечает в Telegram,
// ответы возвращаются сюда (поллинг). GET/POST /projects/{id}/consultations/{cid}/messages.
import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Send } from 'lucide-react';
import { api } from '@/api/client';
import { Modal } from '@/components/Modal';

interface Msg { id: string; sender: 'customer' | 'coordinator' | 'system'; body: string; tg_from: string | null; created_at: string }
interface Thread { status: string; messages: Msg[] }

const STATUS_LABEL: Record<string, string> = {
  new: 'Новая', quoted: 'Оценка стоимости', answered: 'Отвечено', closed: 'Закрыта',
};

export function ConsultationThreadModal({ projectId, consultationId, onClose }: {
  projectId: string; consultationId: string; onClose: () => void;
}) {
  const qc = useQueryClient();
  const [text, setText] = useState('');
  const endRef = useRef<HTMLDivElement>(null);

  const key = ['consultation-thread', projectId, consultationId];
  const { data } = useQuery<Thread>({
    queryKey: key,
    queryFn: () => api.get<Thread>(`/projects/${projectId}/consultations/${consultationId}/messages`),
    refetchInterval: 5000, // ответ координатора приходит из Telegram — поллим
  });
  const messages = data?.messages || [];

  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [messages.length]);

  // 15.D: открытие треда = прочитано → отметить и сбросить бейдж непрочитанного.
  useEffect(() => {
    api.post(`/projects/${projectId}/consultations/${consultationId}/seen`, {}).catch(() => {});
    qc.invalidateQueries({ queryKey: ['consultations-unread', projectId] });
    qc.invalidateQueries({ queryKey: ['my-consultations', projectId] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const send = useMutation({
    mutationFn: (t: string) => api.post(`/projects/${projectId}/consultations/${consultationId}/messages`, { text: t }),
    onSuccess: () => { setText(''); qc.invalidateQueries({ queryKey: key }); },
  });

  const canSend = text.trim().length > 0 && !send.isPending;

  return (
    <Modal title="Переписка со специалистом" onClose={onClose} maxWidth={560}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <span className="rounded-pill" style={{ padding: '3px 10px', fontSize: 11.5, fontWeight: 600, background: '#F3F4F6', color: '#374151' }}>
          {STATUS_LABEL[data?.status || ''] || data?.status || '—'}
        </span>
        <span className="text-muted" style={{ fontSize: 12 }}>Ответ специалиста придёт сюда автоматически.</span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 380, overflowY: 'auto', padding: '4px 2px' }}>
        {messages.length === 0 && (
          <p className="text-muted" style={{ fontSize: 13, textAlign: 'center', padding: '24px 0' }}>Сообщений пока нет.</p>
        )}
        {messages.map((m) => {
          const mine = m.sender === 'customer';
          const sys = m.sender === 'system';
          if (sys) {
            return <div key={m.id} className="text-muted" style={{ fontSize: 11.5, textAlign: 'center', padding: '4px 0' }}>{m.body}</div>;
          }
          return (
            <div key={m.id} style={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start' }}>
              <div style={{
                maxWidth: '78%', padding: '8px 12px', borderRadius: 12, fontSize: 13, lineHeight: 1.5, whiteSpace: 'pre-wrap',
                background: mine ? '#0A1628' : '#F3F4F6', color: mine ? '#fff' : '#0A1628',
                borderTopRightRadius: mine ? 4 : 12, borderTopLeftRadius: mine ? 12 : 4,
              }}>
                {!mine && m.tg_from && (
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#B45309', marginBottom: 2 }}>{m.tg_from}</div>
                )}
                {m.body}
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 14, alignItems: 'flex-end' }}>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && canSend) send.mutate(text.trim()); }}
          rows={2}
          maxLength={8000}
          placeholder="Сообщение специалисту… (⌘+Enter — отправить)"
          style={{ flex: 1, fontSize: 13, lineHeight: 1.5, padding: '9px 12px', border: '1px solid #CBD5E1', borderRadius: 8, outline: 'none', resize: 'vertical', boxSizing: 'border-box' }}
        />
        <button type="button" onClick={() => canSend && send.mutate(text.trim())} disabled={!canSend}
          className="inline-flex items-center justify-center rounded-btn"
          style={{ width: 44, height: 40, border: 'none', background: canSend ? '#F59E0B' : '#F3F4F6', color: canSend ? '#1F1300' : '#9CA3AF', cursor: canSend ? 'pointer' : 'not-allowed', flexShrink: 0 }}
          aria-label="Отправить">
          <Send size={17} />
        </button>
      </div>
    </Modal>
  );
}
